if (!customElements.get("quick-add-bulk")) {
  customElements.define(
    "quick-add-bulk",
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector("quantity-input");

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(
              event.target.dataset.index,
              parseInt(event.target.value)
            );
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener("input", debouncedOnChange.bind(this));
        this.addEventListener("change", debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.listenForCartonAdd();
        this.listenForUnitAdd();
        this.removeFromCart();
        this.lastActiveInputId = null;
        const pageParams = new URLSearchParams(window.location.search);
        window.pageNumber = decodeURIComponent(pageParams.get("page") || "");
        window.pageParams = decodeURIComponent(pageParams);
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (
              event.source === "quick-add" ||
              (event.cartData.items &&
                !event.cartData.items.some(
                  (item) => item.id === parseInt(this.dataset.index)
                )) ||
              (event.cartData.variant_id &&
                !(event.cartData.variant_id === parseInt(this.dataset.index)))
            ) {
              return;
            }
            // If its another section that made the update
            this.onCartUpdate().then(() => {
              this.listenForActiveInput();
              this.listenForKeydown();
              this.listenForCartonAdd();
              this.listenForUnitAdd();
              this.removeFromCart();
            });
          }
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      getInput() {
        return this.querySelector("quantity-input input");
      }

      getCardStatus() {
        const closestCardContent = this.closest(".card__content");
        return closestCardContent.querySelector(".card-status");
      }

      selectProgressBar() {
        return this.querySelector(".progress-bar-container");
      }

      listenForActiveInput() {
        if (!this.classList.contains("hidden")) {
          this.getInput().addEventListener("focusin", (event) =>
            event.target.select()
          );
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.getInput().addEventListener("keydown", (event) => {
          if (event.key === "Enter") {
            this.getInput().blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          "keypress",
          () => {
            event.target.setCustomValidity("");
          },
          { once: true }
        );
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          // Find the parent element with data-id (can be .collection or .collection-quick-add-bulk)
          const parentWithId =
            this.closest(".collection") ||
            this.closest(".collection-quick-add-bulk") ||
            this.closest("[data-id]");

          if (!parentWithId || !parentWithId.dataset.id) {
            console.warn(
              "Could not find parent element with data-id for quick-add-bulk update"
            );
            resolve();
            return;
          }

          fetch(
            `${this.getSectionsUrl()}?section_id=${parentWithId.dataset.id}`
          )
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(
                responseText,
                "text/html"
              );
              const sourceQty = html.querySelector(
                `#quick-add-bulk-${this.dataset.id}-${parentWithId.dataset.id}`
              );
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      async mutateCart(
        items,
        sectionsToRender,
        displayStockWarning,
        isFallback = false
      ) {
        const ids = Object.keys(items);

        // Commented-out as progress bar is now handled from within the `handleLoadingIndicator` fn.
        // this.selectProgressBar().classList.remove('hidden');
        const body = JSON.stringify({
          updates: items,
          sections: sectionsToRender,
          sections_url: this.getSectionsUrl(),
        });

        try {
          const response = await fetch(`${routes.cart_update_url}`, {
            ...fetchConfig(),
            ...{ body },
          });
          const state = await response.text();
          let parsedState = JSON.parse(state);

          const isBatchRequest = ids.length > 1;
          if (isFallback) {
            return parsedState;
          }
          if ("errors" in parsedState && isBatchRequest) {
            for (const itemEntry of Object.entries(items)) {
              // TODO: remove or significantly decrease this "delay" when Shopify boosts the AJAX cart API throttling limits
              // await new Promise((resolve) => setTimeout(resolve, 500));

              const fallbackItems = Object.fromEntries([itemEntry]);
              await this.mutateCart(
                fallbackItems,
                [],
                displayStockWarning,
                true
              );
            }

            // TODO: remove or significantly decrease this "delay" when Shopify boosts the AJAX cart API throttling limits
            // await new Promise((resolve) => setTimeout(resolve, 500));

            parsedState = await this.mutateCart(
              {},
              sectionsToRender,
              displayStockWarning,
              false
            );
          }

          this.renderSectionsGlobal(parsedState, ids, displayStockWarning);

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "quick-add",
            cartData: parsedState,
          });

          return parsedState;
        } catch (error) {
          console.error(error, { isFallback });
          // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
          // e.target.setCustomValidity(error);
          // e.target.reportValidity();
          // this.resetQuantityInput(ids[index]);
          // this.selectProgressBar().classList.add('hidden');
          // e.target.select();
          // this.cleanErrorMessageOnType(e);
          return null;
        } finally {
          if (!isFallback) {
            // Commented-out as progress bar is now handled from within the `handleLoadingIndicator` fn.
            // this.selectProgressBar().classList.add('hidden');
            this.requestStarted = false;
            BulkAdd.globalState.requestStarted = false;
          }
        }
      }

      updateMultipleQty(items) {
        // Check if the quantity of the variant is greater than the inventory quantity
        let displayStockWarning = false;

        Object.keys(items).forEach((variantId) => {
          const inputElement = document.querySelector(
            `[data-quantity-variant-id="${variantId}"]`
          );
          if (inputElement) {
            const inventoryQuantity = parseInt(
              inputElement.dataset.inventoryQuantity
            );
            if (!isNaN(inventoryQuantity)) {
              if (items[variantId] > inventoryQuantity) {
                items[variantId] = inventoryQuantity;
                displayStockWarning = true;
              }
            }
          }
        });

        this.selectProgressBar().classList.remove("hidden");
        const ids = Object.keys(items);
        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map(
            (section) => section.section
          ),
          sections_url: this.getSectionsUrl(),
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => {
            return response.text();
          })
          .then(async (state) => {
            let parsedState = JSON.parse(state);

            // Update all horizontal cards for all variants that were changed
            for (const variantId of ids) {
              await this.updateHorizontalCard(variantId);
            }

            if (
              (parsedState.items.length < 2 ||
                !this.hasAttribute("data-cart-item")) &&
              window.location.pathname.includes("/cart")
            ) {
              await fetch(`${this.getSectionsUrl()}?section_id=main-cart-items`)
                .then((response) => response.text())
                .then((responseText) => {
                  const html = new DOMParser().parseFromString(
                    responseText,
                    "text/html"
                  );
                  parsedState.sections[
                    document.getElementById("main-cart-items").dataset.id
                  ] = html.getElementById("main-cart-items").outerHTML;
                });
            }

            this.renderSections(parsedState, ids, displayStockWarning);

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "quick-add",
              cartData: parsedState,
            });
          })
          .catch((e) => {
            // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
            // e.target.setCustomValidity(error);
            // e.target.reportValidity();
            // this.resetQuantityInput(ids[index]);
            // this.selectProgressBar().classList.add('hidden');
            // e.target.select();
            // this.cleanErrorMessageOnType(e);
          })
          .finally(() => {
            this.selectProgressBar().classList.add("hidden");
            this.requestStarted = false;
            BulkAdd.globalState.requestStarted = false;
          });
      }

      getSectionsToRender() {
        const sections = [
          {
            id: "cart-icon-bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
        ];

        if (window.location.pathname.includes("/cart")) {
          sections.push(
            {
              id: "CartDrawer",
              selector: "#CartDrawer",
              section: "cart-drawer",
            },
            {
              id: "main-cart-footer",
              section: document.getElementById("main-cart-footer").dataset.id,
              selector: "#main-cart-footer",
            }
          );

          // On cart page, we'll handle featured collection cards individually
          // No need to add the entire featured collection section
        } else {
          // For non-cart pages, we'll handle featured collection cards individually
          // No need to add the entire featured collection section
        }

        return sections;
      }

      handleLoadingIndicator() {
        const globalState = BulkAdd.globalState;

        // Show progress bar
        const quickBulkElement =
          this.closest("quick-add-bulk") || this.closest("quick-order-list");
        const progressBarContainer = quickBulkElement.querySelector(
          ".progress-bar-container"
        );

        if (progressBarContainer) {
          progressBarContainer.classList.remove("hidden");
        }

        // Hide progress bar when finished
        const interval = setInterval(() => {
          const index = this.dataset.index;
          const scheduledOrRunning =
            globalState.queue.some((item) => item.id === index) ||
            globalState.requestStarted;
          if (scheduledOrRunning) {
            return;
          }

          const progressBarContainer = quickBulkElement.querySelector(
            ".progress-bar-container"
          );
          if (progressBarContainer) {
            progressBarContainer.classList.add("hidden");
          }

          clearInterval(interval);
        }, 100);
      }

      renderSections(parsedState, ids, displayStockWarning) {
        const sectionsToRender = this.getSectionsToRender();
        if (
          parsedState.sections?.[
            document.getElementById("main-cart-items")?.dataset.id
          ]
        ) {
          sectionsToRender.push({
            id: "main-cart-items",
            section: document.getElementById("main-cart-items").dataset.id,
            selector: "#main-cart-items",
          });
        }

        sectionsToRender.forEach((section) => {
          const sectionElement = document.getElementById(section.id);

          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;

          if (elementToReplace) {
            try {
              if (parsedState.sections?.[section.section]) {
                elementToReplace.innerHTML = this.getSectionInnerHTML(
                  parsedState.sections[section.section],
                  section.selector
                );
              }

              if (
                elementToReplace.matches(section.selector) &&
                parsedState.newParsedSections?.[section.selector]
              ) {
                elementToReplace.innerHTML =
                  parsedState.newParsedSections[section.selector];
              }
            } catch (error) {
              console.error(
                `Error updating section ${section.section}: ${error}`
              );
            }
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
        this.listenForCartonAdd();
        this.listenForUnitAdd();
        this.removeFromCart();
      }

      listenForCartonAdd() {
        const addCartonButton = this.querySelector("[name='add-carton']");

        if (!addCartonButton) return;

        addCartonButton.addEventListener("click", (event) => {
          event.preventDefault();

          const input = this.getInput();
          const cartonSize = addCartonButton.getAttribute("data-amount", false);
          const value = parseInt(input.value);
          input.value = Number(value) + Number(cartonSize);
          input.dispatchEvent(new Event("input", { bubbles: true }));
        });
      }

      listenForUnitAdd() {
        const addUnitButton = this.querySelector("[name='add-unit']");

        if (!addUnitButton) return;

        addUnitButton.addEventListener("click", (event) => {
          event.preventDefault();

          const input = this.getInput();
          const unitSize = addUnitButton.getAttribute("data-amount", false);
          const value = parseInt(input.value);
          input.value = Number(value) + Number(unitSize);
          input.dispatchEvent(new Event("input", { bubbles: true }));
        });
      }

      removeFromCart() {
        const closestCardContent = this.closest(".card__content");
        const removeFromCartButton = closestCardContent?.querySelector(
          ".card-status [name='cart-remove']"
        );

        if (!removeFromCartButton) return;

        removeFromCartButton.addEventListener("click", (event) => {
          event.preventDefault();
          const cardContent = event.target.closest(".card__content");
          const closestQuickAdd = cardContent.querySelector("quick-add-bulk");
          const input = closestQuickAdd.getInput();
          input.value = 0;
          input.dispatchEvent(new Event("input", { bubbles: true }));
        });
      }

      async updateHorizontalCard(variantId) {
        // Find all horizontal cards for this variant across the page
        const horizontalCards = document.querySelectorAll(
          `.card-horizontal quick-add-bulk[data-index="${variantId}"]`
        );

        if (horizontalCards.length === 0) return;

        try {
          // Get the product URL from the first card to fetch fresh data
          const productUrl =
            horizontalCards[0].getAttribute("data-product-url");
          if (!productUrl) return;

          const response = await fetch(
            `${productUrl}?section_id=quick-order-list`
          );
          const html = await response.text();
          const parsedHtml = new DOMParser().parseFromString(html, "text/html");
          const freshCard = parsedHtml.querySelector(
            ".quick-order-list-container .card-horizontal"
          );

          if (freshCard) {
            // Update all horizontal cards for this variant
            horizontalCards.forEach((cardQuickAdd) => {
              const oldCard = cardQuickAdd.closest(".card-horizontal");
              const currentFreshCard = freshCard.cloneNode(true);
              if (
                oldCard
                  .querySelector("quick-add-bulk")
                  .hasAttribute("data-cart-item")
              ) {
                currentFreshCard
                  .querySelector("quick-add-bulk")
                  .setAttribute("data-cart-item", "true");
              }

              if (oldCard) {
                if (
                  currentFreshCard.querySelector("quantity-input input")
                    .value == 0 &&
                  this.closest("cart-items")
                ) {
                  oldCard.remove();
                  this.closest(".card-horizontal")
                    .nextElementSibling?.querySelector("quantity-input input")
                    ?.focus();
                } else {
                  oldCard.replaceWith(currentFreshCard.cloneNode(true));
                }
              }
            });
          }
        } catch (error) {
          console.error("Error updating horizontal card:", error);
        }
      }
    }
  );
}
